// External Libraries
import React, { useState, useEffect } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
    AppBar,
    Box,
    Toolbar,
    TextField,
    Modal,
    Table,
    TableCell,
    TableBody,
    TableFooter,
    Checkbox,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    CssBaseline,
    Grid,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    Stack,
    Chip,
    Autocomplete,
    useMediaQuery,
    Pagination,
    Tooltip,
    InputAdornment,
    Fab,
} from '@mui/material';

// Date picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';

// Icons
import ApprovalIcon from '@mui/icons-material/Approval';
import Close from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import HelpIcon from '@mui/icons-material/ContactSupport';
import Cancel from '@mui/icons-material/CancelOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import Searchicon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import DoneOutlinedIcon from '@mui/icons-material/Pending'; //single
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

// Utilities
import dayjs from 'dayjs';
import 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import qrcode from 'qrcode';
import { useTranslation } from 'react-i18next';

// Custom Components
import Header from '../../../Layout/Header';
import CallAlertBar from '../../../Layout/Alertbar';
import QualityCheckRejectProcee from './QCRejectReel';
import ProductionBreakProcess from '../break/ProductionBreak';
import ParentRollActionMenu from './ActionMenu';
import WinderMachineProduction from '../winderprocess/WinderMachineProduction';
import ConfirmationMessage from '../../../components/validation/ConfirmationMessage';
import ViewParentRollReport from '../reports/ParentRollBooking';
import ListOfChildRolls from './ListOutChildRoll';
import LabelParentRollDetails from '../labels/ParentRoll';
import QCPassModel from '../qc/QCPassParentRoll';

// API and configuration
import { tokens } from '../../../theme/theme';

import { hasPermission } from '../../../components/user-permission/AuthorizationParameter';
import useUserPermissions from '../../../components/user-permission/UserPermission';

import GlobalConfig from '../../../config/date/GlobalConfig';
import SkeletonTableLoader from '../../../components/table/SkeletonTableLoader';
import CustomTableContainer from '../../../components/table/CustomTableContainer';
import useRemoveMANumberByParentRollId from '../../../hooks/useRemoveMAInParentRollById';
import useAddMANumberByParentRollId from '../../../hooks/useAddMANumberByParentRollId';
import ResponsiveBox from '../../../components/form/ResponsiveScrollBox';
import CustomPagination from '../../../components/table/CustomPagination';
import DialogBox from '../../../components/form/DialogBox';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { callApi } from '../../../utils/apiClient';
import { API_ENDPOINTS } from '../../../config/api/apiCall';
import TableFilterRow from '../../../components/table/TableFilterRow';
// Existing imports
//import Autocomplete from '@mui/material/Autocomplete';
// import InputAdornment from '@mui/material/InputAdornment';
// import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddRemarks from '../../master/AddRemarks';
import useRemarks from '../../../hooks/common/useRemarks';



const HotkeyInfoBox = () => {
    const theme = useTheme();
    return (

        <Box
            position="absolute"
            top="50px"
            right="50px"
            padding="10px"
            border="1px solid #ccc"
            borderRadius="5px"
            backgroundColor={theme.palette.background.default}
            boxShadow="0 0 10px rgba(0, 0, 0, 0.1)"
        >
            <Typography variant="body1">Hotkey Information:</Typography>
            <Divider />
            <Typography variant="body2">Alt + P: Print</Typography>
            <Typography variant="body2">Alt + A: Print All</Typography>
            <Typography variant="body2">Alt + B: Booking</Typography>
        </Box>
    );
};


// calculate changed MA Quantity and Reel Length value
function calculateSelectedRowValue(rows) {
    let selected_ma_qty = 0.0;
    let selected_reel_length = 0.0;

    rows.forEach(row => {
        if (row.selectitem) {
            selected_ma_qty += parseFloat(row.ma_qty);
            selected_reel_length += parseFloat(row.reel_length);
        }
    });
    if (selected_ma_qty > 0) {
        selected_ma_qty = selected_ma_qty.toFixed(3);
    }
    return { selected_ma_qty, selected_reel_length };
}

// Function to calculate reel length based on MA Qty
function calculateReelLength(row) {
    let ma_quantity = (row.ma_qty * 1000); // Converts the input value to meters

    // Handle gsm input - take the first value if multiple values exist
    let gsm = row.gsm.includes(",") ? parseFloat(row.gsm.split(",")[0]) : parseFloat(row.gsm);

    let reelLength = (ma_quantity * 100000) / (gsm * row.jumbowidth);
    return Math.floor(reelLength); // Round down to the nearest integer
};



function calculateQuantity(row) {
    let gsm = row.gsm.includes(",") ? parseFloat(row.gsm.split(",")[0]) : parseFloat(row.gsm);
    let reelWeight = (row.reel_length * gsm * row.jumbowidth) / 100000000;
    return reelWeight.toFixed(3); // Round to 3 decimal places for better precision
};

// calculate reel-length value
function calculate_reel_length_row_wise(data) {
    let selected_ma_qty = 0;

    data.forEach(row => {
        selected_ma_qty += row.ma_qty; // Accumulate the ma_qty
        const reelLength = calculateReelLength(row); // Calculate reel_length for the row
        row.reel_length = reelLength; // Assign the calculated reel_length back to the row
    });

    // Fix the precision of the accumulated ma_qty after summing
    selected_ma_qty = parseFloat(selected_ma_qty.toFixed(3));

    // Update the state with the total ma_qty
    // setTotalMAQuantity(selected_ma_qty);
    return selected_ma_qty;
};



const PaperRollProduction = ({ handleBackWinderMachineEntry }) => {

    // Theme and tokens
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const userPermissions = useUserPermissions();

    // Parent Roll  Alert Values
    const [parentRollMsgOpen, setParentRollMsgOpen] = useState(false);
    const [parentRollSeverity, setParentRollSeverity] = useState('info');
    const [parentRollMessage, setParentRollMessage] = useState('Welcome to Paper Roll Booking! ');
    const [parentRollAlertTitle, setParentRollAlertTitle] = useState('');


    const callError = (serverityval, msg, title) => {
        // Update state with error message        
        setParentRollSeverity(serverityval);
        setParentRollMessage(msg);
        setParentRollAlertTitle(title);
        setParentRollMsgOpen(true);
    };

    // Date range States
    const [fromDate, setFromDate] = useState(dayjs().subtract(GlobalConfig.parentrollstartdate, 'day').format('DD/MMM/YYYY'));
    const [toDate, setToDate] = useState(dayjs().format('DD/MMM/YYYY'));

    // shift code 
    const [shiftCodes, setShiftCodes] = useState([]);
    const [selectedShiftCode, setSelectedShiftCode] = useState(null);
    const [shiftID, setShiftID] = useState(0);

    // Data loading States
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [machines, setMachines] = useState([]);
    const [machineID, setMachineID] = useState(0);
    const [machineCode, setMachineCode] = useState('');
    const [isMachineDataLoaded, setIsMachineDataLoaded] = useState(false);

    // Lot and MO (Manufacturing Order) details States
    const [moHdrDetail, setMOHdrDetail] = useState([]);
    const [lotNumbers, setLotNumbers] = useState([]);
    const [selectedLotNumber, setSelectedLotNumber] = useState([]);
    const [lotDetail, setLotDetail] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectMO, SetSelectMo] = useState(0);
    const [selectLot, SetSelectLot] = useState(0);
    const [shiftDate, setShiftDate] = useState(dayjs());
    // const [remarks, setRemarks] = useState('');
    const [status, setStatus] = useState('');


    // Parent Roll details and related States
    const [parentRollNumber, setParentRollNumber] = useState('');
    const [enableBreak, setEnableBreak] = useState(false);
    const [breakReason, setBreakReason] = useState('');
    const [rejectReel, setRejectReel] = useState(false);
    const [editParentRollDetail, setEditParentRollDetail] = useState([]);
    const [childRollList, setChildRollList] = useState([]);

    // QR Code States
    const [qrCodeValue, setQRCodeValue] = useState('Netique infotech');

    // View and button states
    const [viewMOList, setViewMOList] = useState(false);
    const [viewMAList, setViewMAList] = useState(false);
    const [viewQRCode, setViewQRCode] = useState(false);
    const [viewWinderProduction, setViewWinderProduction] = useState(false);
    const [isSaveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [islotnumberDisabled, setLotnumberDisabled] = useState(false);
    const [viewProductionDetail, setViewProductionDetail] = useState(false);

    // Rejection States
    const [rejectReason, setRejectReason] = useState([]);
    const [selectedReason, setSelectedReason] = useState([]);
    const [rejectProductType, setRejectProductType] = useState(null);
    const [rejectStatus, setRejectStatus] = useState(false);
    const [rejectReasonID, setRejectReasonID] = useState(0);
    const [rejectRemarks, setRejectRemarks] = useState(null);
    const [enableReject, setEnableReject] = useState(false);
    const [selectedButton, setSelectedButton] = useState(null);
    const [openBreak, setOpenBreak] = useState(false);

    // error message fields 
    const [MO_errorMessage, setMO_errorMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [shiftdate_errorMessage, setshiftdate_errorMessage] = useState('');
    const [papermachine_errorMessage, setpapermachine_errorMessage] = useState('');
    const [jumbowidth_errorMessage, setjumbowidth_errorMessage] = useState('');
    const [quality_errorMessage, setquality_errorMessage] = useState('');
    const [gsm_errorMessage, setgsm_errorMessage] = useState('');
    const [reelod_errorMessage, setreelod_errorMessage] = useState('');
    const [coreid_errorMessage, setcoreid_errorMessage] = useState('');
    const [quantity_errorMessage, setquantity_errorMessage] = useState('');
    const [MAquantity_errorMessage, setMAquantity_errorMessage] = useState('');
    const [noofsets_errorMessage, setnoofsets_errorMessage] = useState('');
    const [reellength_errorMessage, setreellength_errorMessage] = useState('');


    // View Report States
    const [viewReport, setViewReport] = useState(false);
    const [openChildRollList, setOpenChildRollList] = useState(false);
    const [openMAWiseBalanceList, setMAWiseBalanceList] = useState(false);

    // MA Quantity and Reel Length States
    const [totalMAQuantity, setTotalMAQuantity] = useState(0.0); // State to store total MA quantity
    const [totalReelLength, setTotalReelLength] = useState(0); // State to store total MA quantity

    // Selection States
    const [selectedMOs, setSelectedMOs] = useState([]);
    const [lotDetails, setLotDetails] = useState([]);
    const [showHotkeyInfo, setShowHotkeyInfo] = useState(false);

    // create date range
    const [openDateRangeDialog, setOpenDateRangeDialog] = useState(false);
    const [selectedGSM, setSelectedGSM] = useState('');
    const [selectedQuality, setSelectedQuality] = useState('');
    const [viewParentRollList, setViewParentRollList] = useState(true);
    const [addParentRoll, setAddParentRoll] = useState(false);
    const [viewQCPassModel, setViewQCPassModel] = useState(false);

    // First Menu States
    const [anchorElFirst, setAnchorElFirst] = useState(null);
    const [selectedOptionFirst, setSelectedOptionFirst] = useState('All Parent Roll List');
    const openFirst = Boolean(anchorElFirst);

    // Party Details States
    const [partyList, setPartyList] = useState([]);
    const [editPartyID, setEditPartyID] = useState(0);
    const [isEdit, setIsEdit] = useState(false);

    // Second Menu States
    const [anchorElSecond, setAnchorElSecond] = useState(null);
    const openSecond = Boolean(anchorElSecond);

    // Rejection Dialog State
    const userId = localStorage.getItem('userID');
    const [enableRejectParentRoll, setEnableRejectParentRoll] = useState(false);
    const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);

    // Report States
    const [reportShiftCode, setReportShiftCode] = useState('');
    const [reportMachineCode, setReportMachineCode] = useState('');
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);

    // MO Quantity Reduction State
    const [allowReduceMOQty, setAllowReduceMOQty] = useState(true);

    // Selection Rows State
    const [selectedRows, setSelectedRows] = useState([]);
    const [showFirstToolbar, setShowFirstToolbar] = useState(true);
    const [showNewToolbar, setShowNewToolbar] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('All');
    const isSelected = (rowId) => selectedRows.indexOf(rowId) !== -1;


    const [lotDetailsUpdates, setLotDetailsUpdates] = useState(false);
    const [selectedButtonName, setSelectedButtonName] = useState('');

    // skeleton progress 
    const [loading, setLoading] = useState(false);
    const [recordsPerPage, setRecordsPerPage] = useState(20); // Records per page

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    const [filterFromDate, setFilterFromDate] = useState(dayjs().subtract(GlobalConfig.defaultDays, 'day').format('DD/MMM/YYYY'));
    const [filterToDate, setFilterToDate] = useState(dayjs().format('DD/MMM/YYYY'));

    const [pr_status, setPR_status] = useState('');

    const { processIndent } = useRemoveMANumberByParentRollId(callError);
    const { updateMA } = useAddMANumberByParentRollId(callError);

    // Responsive breakpoints
    const isXs = useMediaQuery((theme) => theme.breakpoints.only("xs"));
    const isSm = useMediaQuery((theme) => theme.breakpoints.only("sm"));
    const isMd = useMediaQuery((theme) => theme.breakpoints.only("md"));
    const isLg = useMediaQuery((theme) => theme.breakpoints.only("lg"));
    const isXl = useMediaQuery((theme) => theme.breakpoints.only("xl"));



    //Remarks Dropdown
    // Inside the PaperRollProduction component
    const [remarks, setRemarks] = useState(null);    
    const [openAddRemark, setOpenAddRemark] = useState(false);    

    const moduledetails = userPermissions.find(
        (m) =>
            m.module_name === "Paper Machine Production" &&
            m.parent_module_name === "Production"
    );

    const { remarkOptions, loadingRemarks, refetchRemarks } = useRemarks(moduledetails);

    // Update recordsPerPage based on screen size
    useEffect(() => {
        if (isXs) setRecordsPerPage(20);
        else if (isSm) setRecordsPerPage(10);
        else if (isMd) setRecordsPerPage(10);
        else if (isLg) setRecordsPerPage(15);
        else if (isXl) setRecordsPerPage(27);
    }, [isXs, isSm, isMd, isLg, isXl]);


    const handleFilterFromDate = (date) => {
        setFilterFromDate(dayjs(date));
    };

    const handleFilterToDate = (date) => {
        setFilterToDate(dayjs(date));
    };


    const handleFromDateChange = (date) => {
        setFromDate(dayjs(date));
    };

    const handleToDateChange = (date) => {
        setToDate(dayjs(date));
    };


    // Handlers for first menu
    const handleMenuClickFirst = (event) => {
        setAnchorElFirst(event.currentTarget);
    };

    const handleMenuCloseFirst = () => {
        setAnchorElFirst(null);
    };
    // Code implementation continues...
    const handleOpenAddRemark = () => setOpenAddRemark(true);
    const handleCloseAddRemark = () => {
        setOpenAddRemark(false);
        refetchRemarks();
    };



    // useEffect(() => {
    //     setLoadingRemarks(true);

    //     if (userPermissions.length > 0) {
    //         const moduledetails = userPermissions.find(
    //             (m) =>
    //                 m.module_name === "Paper Machine Production" &&
    //                 m.parent_module_name === "Production"
    //         );
    //         if (moduledetails) {
    //             // const params = {
    //             //     p_companyid: localStorage.getItem("companyid"),
    //             //     module_id: moduledetails.module_id,
    //             //     parent_module_id: moduledetails.parent_module_id
    //             // };
    //             // setParentModuleId(moduledetails.parent_module_id);
    //             // setModuleId(moduledetails.module_id);

    //             // callApi(API_ENDPOINTS.MASTER_GET_REMARKS, "POST", { "Content-Type": "application/json" }, params)
    //             //     .then((data) => {
    //             //         if (data && Array.isArray(data)) {
    //             //             setRemarkOptions(data);
    //             //         }
    //             //     })
    //             //     .catch((err) => {
    //             //         console.error("Failed to load remarks", err);
    //             //         setRemarkOptions([]);
    //             //     })
    //             //     .finally(() => setLoadingRemarks(false));
    //         }
    //     }
    // }, [moduleId, parentModuleId, openAddRemark, userPermissions]);


    const CustomTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} arrow placement="right-start" />
    ))({
        [`& .MuiTooltip-tooltip`]: {
            backgroundColor: colors.blueAccent[500],
            color: 'white',
            fontSize: '14px',
            borderRadius: '10px',
            padding: '10px',

        },
        [`& .MuiTooltip-arrow`]: {
            color: colors.blueAccent[500],
        },
    });

    const handleMenuItemClickFirst = (option) => {

        // Map the options to status
        const statusMap = {
            'All Parent Roll List': 'All',
            'Inprogress Parent Roll': 'booked',
            'Cancelled Parent Roll': 'cancelled',
        };

        setSelectedOptionFirst(option); // Update the selected option
        setSelectedStatus(statusMap[option] || selectedStatus);
        handleMenuCloseFirst();// Close the menu        
    };

    // Handlers for second menu
    const handleMenuClickSecond = (event) => {
        setAnchorElSecond(event.currentTarget); // Open the second menu
    };

    const handleMenuCloseSecond = () => {
        setAnchorElSecond(null); // Close the second menu
    };

    // Refactor handle menu item click for the second menu
    const handleMenuItemClickSecond = (option) => {
        // You can add specific logic for different options here
        //console.log(`Selected Option: ${option}`);  // Optional: Log the selected option
        handleMenuCloseSecond(); // Close the menu
    };

    // Load all parent roll list when the data is not loaded
    useEffect(() => {
        if (!isDataLoaded) {
            GetParentRollList();
        }
    }, [isDataLoaded]); // Re-run when isDataLoaded changes

    // const [isDataLoaded, setIsDataLoaded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const p_companyid = localStorage.getItem('companyid');
            const p_frommodate = dayjs(filterFromDate).format('YYYY-MM-DD hh:mm:ss');
            const p_tomodate = dayjs(filterToDate).format('YYYY-MM-DD hh:mm:ss');

            const para = {
                p_companyid: p_companyid,
                p_frommodate,
                p_tomodate,
                page: currentPage,
                pageSize: pageSize,
            };
            setLoading(true);
            try {
                const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_PRODUCED_PARENT_ROLL, 'POST', { 'Content-Type': 'application/json' }, para);
                if (data) setPartyList(data); // Set the fetched data to party list
            } catch (error) {
                alert(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentPage, pageSize, filterFromDate, filterToDate, setFilterFromDate, setFilterToDate]);


    // Load parent roll list from API
    const GetParentRollList = async () => {
        const p_companyid = localStorage.getItem('companyid');
        const p_frommodate = dayjs(filterFromDate).format('YYYY-MM-DD hh:mm:ss');
        const p_tomodate = dayjs(filterToDate).format('YYYY-MM-DD hh:mm:ss');
        const para = {
            p_companyid: p_companyid,
            p_frommodate,
            p_tomodate,
            page: currentPage,
            pageSize: pageSize,
        };
        setLoading(true);
        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_PRODUCED_PARENT_ROLL, 'POST', { 'Content-Type': 'application/json' }, para);

            if (data?.length) {
                setPartyList(data); // Set the fetched data to party list
                setIsDataLoaded(true); // Mark the data as loaded
                setLoading(false);
            } else {
                // Optionally handle empty data case
                setIsDataLoaded(true); // Ensure it's not reloaded again unnecessarily
                setLoading(false);
            }
        } catch (error) {
            callError('error', error.message, 'Error'); // Handle API call error
        }
    };


    // load Mo Details
    const LoadSelectedMOWiseLotNumber = async () => {

        // Extract an array of selected "MO" numbers
        const selectedMonos = selectedOptions.map((item) => item.mono);
        const orderData = {
            p_companyid: localStorage.getItem('companyid'),
            p_mono: selectedMonos,
        };

        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_LOTNUMBER, 'POST', { 'Content-Type': 'application/json' }, orderData);
            if (data?.length) {
                // Update state with fetched data
                setLotNumbers(data);
                setViewMAList(true);
            }
        } catch (error) {
            callError('error', error.message, 'Error');
            setViewProductionDetail(false);
            setViewQRCode(false);
        }

    };

    const handleLotNumberChange = (event, value) => {

        setSelectedLotNumber(value);
        SetSelectMo(value.mono);
        SetSelectLot(value.lotnumber);
        setParentRollMsgOpen(false);

        const selectedMonos = value.map((item) => item.mono);
        const selectedLotNumbers = value.map((item) => item.lotnumber);

        LoadLotWiseDetails(selectedMonos, selectedLotNumbers, '');

    };

    // load lot wise detail process edit call
    const LoadLotWiseDetails = async (selectedMonos, selectedLotNumbers, parent_roll_number) => {

        const p_companyid = localStorage.getItem('companyid');
        let selected_mode = 0;

        let pr_id = parent_roll_number;

        if (parent_roll_number != '') {
            selected_mode = 1;
        }
        if (editPartyID > 0) {
            pr_id = editPartyID;
        }

        if (selectedMonos.length > 0) {

            const parameter = {
                p_companyid,
                p_mono: selectedMonos, // Pass array of selected MO numbers
                p_lotnumber: selectedLotNumbers, // Pass array of selected lot numbers
                p_mode: selected_mode,
                p_parent_roll_id: pr_id
            };

            callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_LOTNUMBERDETAILS_PARENT_ROLL, 'POST', { 'Content-Type': 'application/json' }, parameter)
                .then(data => {

                    if (data?.length > 0) {

                        // Update state with fetched data
                        setTotalMAQuantity(calculate_reel_length_row_wise(data));
                        setLotDetail(data);
                        setSaveButtonDisabled(false);
                        setViewProductionDetail(true);
                        setViewQRCode(false);

                        if (editParentRollDetail.length > 0) {
                            // select all
                            const newSelectedRows = data.map((row) => row.id);
                            setSelectedRows(newSelectedRows);

                            let updatedRows;
                            updatedRows = data.map(row => ({ ...row, selectitem: true }));
                            setLotDetail(updatedRows);

                            const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
                            setTotalMAQuantity(selected_ma_qty);
                            setTotalReelLength(selected_reel_length);
                        }

                    } else {
                        // No data found
                        setLotDetail([]);
                        callError('info', 'Unable To Load MA Detail.', 'info');

                        setViewQRCode(false);
                        setViewProductionDetail(false);
                    }
                })
                .catch(error => {
                    // Update state with error message        
                    callError('error', error.message, 'Error');
                    setViewProductionDetail(false);
                    setViewQRCode(false);
                });
        }
        else {
            setLotDetail([]);
            setViewQRCode(false);
            setViewProductionDetail(false);
        }
    };



    const insertParentRollNumber = async () => {
        const p_companyid = localStorage.getItem('companyid');
        const ShiftDate = dayjs(shiftDate).format('YYYY-MM-DD hh:mm:ss');
        let validation_msg = '';
        let isValid = true;

        // Reset error messages
        resetErrorMessages();

        // Select and sort the lot details
        const selectedMODetails = lotDetail
            .filter(row => row.selectitem)
            .sort((a, b) => a.lotnumber - b.lotnumber); // simplified sorting logic

        if (selectedMODetails.length === 0) {
            alert("Please select an MA number to proceed.");
            return;
        }

        // Get unique quality and GSM
        const selectedQuality = [...new Set(selectedMODetails.map(row => row.quality))];
        setSelectedQuality(selectedQuality.join(', '));

        const selectedGSM = [...new Set(selectedMODetails.map(row => row.gsm))];
        setSelectedGSM(selectedGSM.join(', '));

        // Validate parameters
        if (shiftID === undefined || shiftDate === '') {
            isValid = false;
            validation_msg = "Please select shift code and shift date.";
        }

        if (!isValid) {
            callError('warning', validation_msg, 'Info');
            return;
        }
        try {



            let p_reject_remarks = rejectRemarks || "NA";

            if (!shiftID) {
                throw new Error("Shift ID is required.");
            }

            // Prepare save parameters
            const saveParams = {
                p_companyid,
                p_plantid: 1,
                p_shift_id: shiftID,
                p_shiftdate: ShiftDate,
                p_selected_mo_and_ma_list: selectedMODetails,
                p_break_reason: breakReason,
                p_reject_reel: rejectReel,
                P_reject_remarks: p_reject_remarks,
                p_pr_number: '001',//pr_number,
                p_pr_quantity: totalMAQuantity,
                p_paper_machine_id: machineID,
                p_user_name: localStorage.getItem('userID'),
                p_status: 'HOLD',
                p_enable_break: enableBreak,
                p_remarks: remarks.description
            };

            // Insert parent roll details
            const saveResponse = await callApi(
                API_ENDPOINTS.PROD_MANAGEMENT_GET_PARENT_ROLL_INSERTPARENTROLLDET,
                'POST',
                { 'Content-Type': 'application/json' },
                saveParams
            );

            // if (saveResponse.Message === 'Parent Roll details inserted successfully') {
            if (saveResponse.length > 0) {
                let result = saveResponse[0];
                setViewQRCode(true);
                LoadSelectedMOWiseLotNumber();
                setSaveButtonDisabled(true);
                toast.success(`New Parent Roll ${result.parent_roll_number} Added Successfully`, { position: toast.POSITION.BOTTOM_CENTER });
                ParentRollProductDetail(result.parent_roll_number);
                setParentRollNumber(result.parent_roll_number);
                setQRCodeValue(result.parent_roll_number);

            } else {
                throw new Error('Unable to Add Parent Roll Number.');
            }


        } catch (error) {
            // Centralized error handling
            callError('error', error.message, 'Error');
            setViewQRCode(false);
        }

    };



    // Reset error messages
    const resetErrorMessages = () => {
        setMO_errorMessage('');
        setErrorMessage('');
        setshiftdate_errorMessage('');
        setpapermachine_errorMessage('');
        setjumbowidth_errorMessage('');
        setquality_errorMessage('');
        setgsm_errorMessage('');
        setreelod_errorMessage('');
        setcoreid_errorMessage('');
        setquantity_errorMessage('');
        setMAquantity_errorMessage('');
        setnoofsets_errorMessage('');
        setreellength_errorMessage('');
    };

    // const update parent roll number
    const updateParentRollNumber = () => {

        const p_companyid = localStorage.getItem('companyid');
        const parameter = { p_companyid, };
        const ShiftDate = dayjs(shiftDate).format('YYYY-MM-DD hh:mm:ss');
        let validation_msg = '';

        setMO_errorMessage('');
        setErrorMessage('');
        setshiftdate_errorMessage('');
        setpapermachine_errorMessage('');
        setjumbowidth_errorMessage('');
        setquality_errorMessage('');
        setgsm_errorMessage('');
        setreelod_errorMessage('');
        setcoreid_errorMessage('');
        setquantity_errorMessage('');
        setMAquantity_errorMessage('');
        setnoofsets_errorMessage('');
        setreellength_errorMessage('');


        let isvalid = true;

        // Validate parameters
        let isValid = true;

        // const selectedMODetails = lotdetail.filter(row => row.selectitem);
        const selectedMODetails = lotDetail
            .filter(row => row.selectitem)
            .sort((a, b) => {
                if (a.lotnumber < b.lotnumber) return -1;
                if (a.lotnumber > b.lotnumber) return 1;
                return 0;
            });


        if (selectedMODetails.length === 0) {
            alert("please select MA number to proceed");
            return;
        }

        // Get unique selected Quality
        let selectedQuality = [...new Set(selectedMODetails.map(row => row.quality))];

        // Convert the array to a comma-separated string
        let selectedQualityString = selectedQuality.join(', ');

        setSelectedQuality(selectedQualityString);

        // Get unique selected GSM
        let selectedGSM = [...new Set(selectedMODetails.map(row => row.gsm))];

        // Convert the array to a comma-separated string
        let selectedGsmString = selectedGSM.join(', ');
        setSelectedGSM(selectedGsmString);

        if (shiftID === undefined) {
            isValid = false;
            validation_msg = "Please select shift code.";
        }

        if (shiftDate === '') {
            isValid = false;
        }

        if (!isValid) {
            console.error('Invalid parameters');
            callError('warning', validation_msg, 'info');
            return;
        }

        let pr_number = "";

        setQRCodeValue(parentRollNumber);
        pr_number = (parentRollNumber);

        if (pr_number === "") {
            isvalid = false;
        }

        let p_reject_remarks = rejectRemarks

        if (rejectRemarks == null) {
            p_reject_remarks = "NA";
        }

        if (shiftID == null || shiftID == undefined) {
            isvalid = false;
        }
        try {
            if (isvalid) {
                //set save parameters
                const save_parameter = {
                    p_companyid,
                    p_plantid: 1,
                    p_shift_id: shiftID,
                    p_shiftdate: ShiftDate,
                    p_selected_mo_and_ma_list: selectedMODetails,
                    p_break_reason: breakReason,
                    p_reject_reel: rejectReel,
                    P_reject_remarks: p_reject_remarks,
                    p_pr_number: pr_number,
                    p_pr_quantity: totalMAQuantity,// need add
                    p_paper_machine_id: machineID, // need add
                    p_user_name: localStorage.getItem('userID'),
                    p_status: 'HOLD',
                    p_enable_break: enableBreak,
                    p_remarks: remarks ? remarks.description : ''
                    // 'booked',
                };


                callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_PARENT_ROLL_UPDATEPARENTROLLDET, 'POST', { 'Content-Type': 'application/json' }, save_parameter)
                    .then(data => {
                        if (data.Message == 'Parent Roll details updated successfully') {

                            // Update state with fetched data                                   
                            setViewQRCode(true);
                            LoadSelectedMOWiseLotNumber();
                            setSaveButtonDisabled(true);

                            // Show a notification when the PDF is downloaded successfully
                            toast.success('New Parent Roll ' + pr_number + ' Added Successfully', { position: toast.POSITION.BOTTOM_CENTER });
                            ParentRollProductDetail(pr_number);

                        } else {
                            // No data found
                            callError('info', 'Unable To Add Parent Roll Number.', 'info');
                            setViewQRCode(false);
                        }
                    })
                    .catch(error => {
                        // Update state with error message      
                        callError('error', error.message, 'error');
                        setViewQRCode(false);
                    });
            }
        }
        catch (error) {
            callError('error', error.message, 'Error');
            //setViewQRCode(false);
        }

       
    }


    // Fetch shift codes function
    const fetchShiftCodes = async (selectedID = null) => {
        const para = { p_companyid: localStorage.getItem('companyid') };

        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_SHIFT_CODE, 'POST', { 'Content-Type': 'application/json' }, para);

            if (data && data.length > 0) {
                setShiftCodes(data);
                const initialShift = data.find(shift => shift.shiftid === selectedID) || data[0];
                setSelectedShiftCode(initialShift);
                setShiftID(initialShift.shiftid);
                setReportShiftCode(initialShift.shiftcode);

            }
        } catch (error) {
            callError('error', error.message, 'Error');
            setViewMOList(false);
            setViewMAList(false);
            setViewQRCode(false);
            setViewProductionDetail(false);
        }
    };

    // UseEffect to load shift codes on component mount
    useEffect(() => {
        if (!isDataLoaded && editPartyID === 0) {
            fetchShiftCodes();
        }
    }, [isDataLoaded, editPartyID]);

    // Call fetchShiftCodes with selectedID
    const loadShiftCode = async (selectedID) => {
        await fetchShiftCodes(selectedID);
    };


    useEffect(() => {
        if (!isMachineDataLoaded) {
            fetchMachineCode();
        }
    }, [isMachineDataLoaded])

    const updateSelectedMachineIdValue = async (id_machine) => {
        await fetchMachineCode(id_machine);
    };


    const fetchMachineCode = async (id_machine = null) => {
        const para = {
            p_companyid: localStorage.getItem('companyid'),
            p_plantid: 1,
        }
        // 

        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_MACHINE, 'POST', { 'Content-Type': 'application/json' }, para);
            if (data?.length > 0) {
                setMachines(data);
                setMachineCode(data[0]);
                setMachineID(data[0].id);

                const selected = data.find(party => party.id === id_machine);
                const machineToSelect = selected || data[0];

                if (machineToSelect) {
                    setMachineCode(machineToSelect);
                    setMachineID(machineToSelect.id);
                    setReportMachineCode(machineToSelect.machine_code);
                }
            }
        }
        catch (error) {
            callError('error', error.message, 'Error');
            setViewMOList(false);
            setViewMAList(false);
            setViewQRCode(false);
            setViewProductionDetail(false);
        }

    }


    useEffect(() => {
        const fetchData = async () => {
            const selectedMonos = selectedMOs.map((item) => item.mono);

            if (selectedMonos.length > 0) {

                const orderData = {
                    p_companyid: localStorage.getItem('companyid'),
                    p_mono: selectedMonos,
                };

                try {
                    const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_LOTNUMBER, 'POST', { 'Content-Type': 'application/json' }, orderData)
                    if (data?.length > 0) {
                        // Update state with fetched data
                        setLotNumbers(data);
                        setViewMAList(true);
                        setLotnumberDisabled(false);

                    } else {

                        setLotNumbers([]);
                        setViewMAList(false);
                        setLotnumberDisabled(true);
                        // alert('No MA Data are available in this MO.');
                    }

                } catch (error) {
                    callError('error', error.message, 'Error');
                    setViewProductionDetail(false);
                    setViewQRCode(false);
                }

            }
            else {
                setLotNumbers([]);
                setViewMAList(false);
                setLotnumberDisabled(true);
                SetSelectLot(0);
                setSelectedLotNumber([]);
                setLotDetail([]);
                setViewQRCode(false);
                setViewProductionDetail(false);
            }

        };

        fetchData();
    }, [selectedMOs]);


    const handleShiftChange = (event, value) => {
        setSelectedShiftCode(value);
        setShiftID(value.shiftid);
        setReportShiftCode(value.shiftcode);
    }

    const handleMachineChange = (event, value) => {

        if (value != undefined || value != null) {
            setMachineCode(value);
            setMachineID(value.id);
            setReportMachineCode(value.machine_code);
        }
    }



    const filterData = (filter) => {
        // let fromDate, toDate;

        // switch (filter) {
        //     case 'All':
        //         fromDate = dayjs().subtract(4, 'year').startOf('year').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     case 'Today':
        //         fromDate = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     case 'This Week':
        //         fromDate = dayjs().startOf('week').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('week').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     case 'This Month':
        //         fromDate = dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     case 'This Quarter':
        //         fromDate = dayjs().startOf('quarter').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('quarter').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     case 'This Year':
        //         fromDate = dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss');
        //         toDate = dayjs().endOf('year').format('YYYY-MM-DD HH:mm:ss');
        //         break;
        //     default:
        //         fromDate = ''; // You can set a default value here
        //         toDate = ''; // You can set a default value here
        //         break;
        // }

        // setFromDate(dayjs(fromDate));
        // setToDate(dayjs(toDate));


    };



    useEffect(() => {

        const p_companyid = localStorage.getItem('companyid');
        const p_frommodate = dayjs(fromDate).format('YYYY-MM-DD hh:mm:ss');
        const p_tomodate = dayjs(toDate).format('YYYY-MM-DD hh:mm:ss');
        const p_filtertype = "";
        const p_mono = 0;
        setParentRollMsgOpen(false);

        // Collect the values of the controls
        const orderData = {
            p_companyid,
            p_frommodate,
            p_tomodate,
            p_filtertype,
            p_mono,
            p_parent_roll_number: 'NA'
        };

        // Make the API call using the common function'http://localhost:3013/getMODetail'
        callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_MOLIST, 'POST', { 'Content-Type': 'application/json' }, orderData)
            .then(data => {
                if (data?.length > 0) {
                    // Update state with fetched data
                    setMOHdrDetail(data);
                    setViewMOList(true);
                    setViewProductionDetail(false);
                } else {
                    // No data found
                    setMOHdrDetail([]);
                    callError('info', 'No MO are released this date range, Kindly adjust data range.', 'info');
                    // 
                    setViewMOList(false);
                    setViewMAList(false);
                    setViewQRCode(false);
                    setViewProductionDetail(false);
                }
            })
            .catch(error => {
                // Update state with error message        
                callError('error', error.message, 'Error');
                // 
                setViewMOList(false);
                setViewMAList(false);
                setViewQRCode(false);
                setViewProductionDetail(false);
            });


    }, [fromDate, toDate, setFromDate, setToDate]);


    const handleButtonClick = async (buttonName) => {

        setSelectedButton(buttonName);
        setParentRollMsgOpen(false);

        if (buttonName === "BookParentRoll") {
            //handle_get_pr_number()
            insertParentRollNumber()
        }
        if (buttonName === "UpdateParentRoll") {
            updateParentRollNumber()
        }
        else {
            let isvalid = true;

            if (parentRollNumber === '') {
                isvalid = false;
                callError('info', 'Please select parent roll code to print.', 'info');
            }

            if (isvalid == true) {

                const printWindow = window.open('', '_blank');
                try {
                    // Generate HTML content for each QR code parentRollNumber
                    // Generate QR code data URL
                    const qrDataUrl = await generateQRCode(parentRollNumber);
                    // Check if QR code generation was successful
                    if (!qrDataUrl) {
                        console.error('Error generating QR code for value:', parentRollNumber);
                    }
                    // Write HTML content for the QR code to the print window
                    printWindow.document.write(`

                <html>
                    <head>
                        <title>Printed Report</title>
                        <style>
                        body {
                            margin: 0;
                            padding: 0;
                            font-family: Arial, sans-serif;
                        }
                        .container {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            padding: 10px;
                            border: 1px solid #ccc;
                            box-sizing: border-box;
                        }
                        .qr-code {
                            width: 150px; /* Adjust as needed */
                        }
                        .bundle-details {
                            flex: 1;
                            margin-left: 20px; /* Adjust spacing between QR code and bundle details */
                        }
                        .bundle-details h5 {
                            margin: 0;
                            font-size: 18px;
                        }
                        .bundle-details h6 {
                            margin: 5px 0;
                            font-size: 16px;
                        }
                    </style>
                    </head>
                    <body>
                    <div class="container">
                    <div>
                        <img class="qr-code" src="${qrDataUrl}" alt="QR Code" />
                        <h3>${parentRollNumber || 'XXXXXX0123'}</h3>
                    </div>
                    <div class="bundle-details">
                        <div>
                            <h5>Parent Roll Details</h5>
                        </div>
                        <h6>Parent Roll No : ${parentRollNumber || 'XXXXXX0123'}</h6>
                         <h6>Quality : ${selectedQuality || 'XXXXQuality'}</h6>                       
                         <h6>GSM : ${selectedGSM}</h6>
                         <h6>ID/OD :${lotDetail.reel_id || 3}/${lotDetail.reel_od || 0}</h6>
                         <h6>Roll.wt: ${totalMAQuantity || 0}(Mt)</h6>
                    </div>
                </div>
                    </body>
                </html>                
                    
                `);

                    printWindow.document.close();
                    printWindow.print();

                } catch (error) {
                    console.error('Error handling button click:', error);
                }

            }
        }
    };

    const generateQRCode = async (value) => {
        try {
            const qrDataUrl = await qrcode.toDataURL(value);
            return qrDataUrl;
        } catch (error) {
            console.error('Error generating QR code:', error);
            return null;
        }
    };

    const openpopup = () => { setOpenDateRangeDialog(true); }

    const closepopup = () => {

        setOpenDateRangeDialog(false);
        let reject_product_type = "Parentroll"
        let child_roll_number = 0
        let product_code = parentRollNumber
        let isvalid = true;

        if (enableReject) {
            isvalid = false;
        }
        else if (product_code === "") {
            isvalid = false;
        }


        if (isvalid) {

            // call reject reel process
            const parameter = {
                reject_product_type,
                product_code,
                child_roll_number,
                rejectReasonID,
                rejectRemarks,
                companyid: localStorage.getItem('companyid'),
                plantid: 1,
            };

            callApi(API_ENDPOINTS.DECKL_UPDATE_REJECT_REEL, 'POST', { 'Content-Type': 'application/json' }, parameter)
                .then(data => {

                    if (data?.length > 0) {
                        // Update state with fetched data
                        setMOHdrDetail(data);
                        setViewMOList(true);
                        setViewProductionDetail(false);
                    } else {

                        setMOHdrDetail(null);
                        callError('info', 'No MO are released this date range, Kindly adjust data range.', 'info');
                        // 
                        setViewMOList(false);
                        setViewMAList(false);
                        setViewQRCode(false);
                        setViewProductionDetail(false);
                    }
                })
                .catch(error => {

                    callError('error', error.message, 'Error');
                    // 
                    setViewMOList(false);
                    setViewMAList(false);
                    setViewQRCode(false);
                    setViewProductionDetail(false);
                });
        }



    }

    const openBreakpopup = () => {
        setOpenBreak(true);
    }

    const closeBreakpopup = () => {
        setOpenBreak(false);
    }

    const handleMOChange = (event, value) => {
        setSelectedMOs(value);
    };

    const handleSelectAllCheckboxChange = (event) => {
        let updatedRows;
        updatedRows = lotDetail.map(row => ({ ...row, selectitem: event.target.checked }));
        setLotDetail(updatedRows);

        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);

    };

    const areAllRowsSelected = () => {
        return lotDetail.every(row => row.selectitem);
    };

    const handleCheckboxChange = (event, rowId) => {
        const updatedRows = lotDetail.map(row =>
            row.id === rowId ? { ...row, selectitem: event.target.checked } : row
        );
        setLotDetail(updatedRows);

        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);
    };

    // Function to handle changes in the MA Qty field
    const handleMaQtyChange = (event, id) => {

        setParentRollMsgOpen(false);
        const updatedRows = lotDetail.map(row => {
            if (row.id === id) {
                const newMaQty = (event.target.value); // Parse the input value to an integer

                if (newMaQty >= row.qty) {
                    row.ma_qty = newMaQty; // Update the MA Qty for the specific row                
                }
                else {
                    if (allowReduceMOQty) {

                        if (newMaQty <= 0) {
                            row.ma_qty = row.balance_qty; // Update the MA Qty for the specific row
                            callError('error', 'MA Quantity Should be greter then zero.', 'Error');
                        }
                        else {
                            row.ma_qty = newMaQty; // Update the MA Qty for the specific row                           
                        }
                    }
                    else {
                        row.ma_qty = row.qty; // Update the MA Qty for the specific row
                        callError('error', 'MA Quantity Should be greter then or equal.less then value not allowed.', 'Error');
                    }
                }

                // Calculate and update the reel length for the specific row
                const reelLength = calculateReelLength(row); // Implement this function according to your logic
                row.reel_length = reelLength;

                return row;
            }
            return row;
        });

        setLotDetails(updatedRows);
        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);
    };


    // Function to handle changes in reel length

    const handleReelLengthChange = (event, id) => {
        setParentRollMsgOpen(false);

        const updatedRows = lotDetail.map(row => {

            if (row.id === id) {

                const newMaQty = (event.target.value); // Parse the input value to an integer               

                // Calculate and update the reel length for the specific row
                const reelqty = calculateQuantity(row); // Implement this function according to your logic
                row.ma_qty = reelqty;
                return row;
            }
            return row;
        });

        setLotDetails(updatedRows);
        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);
    };

    const handleChangeRemarksVal = (event, id) => {
        const updatedRows = lotDetail.map(row => {
            if (row.id === id) {
                const remarks = (event.target.value); // Parse the input value to an integer             
                row.remarks = remarks;
                return row;
            }
            return row;
        })
        setLotDetails(updatedRows);
    };
    const handleChangeRemarksValTable = (id, field, value) => {
        const updatedRows = lotDetail.map(row => {
            if (row.id === id) {
                const remarks = value; // Parse the input value to an integer             
                row.remarks = remarks;
                return row;
            }
            return row;
        })
        setLotDetails(updatedRows);
    };


    //     const updateReelField = (reelId, field, value) => {
    //     setReelFormData(prev =>
    //         prev.map(item =>
    //             item.id_reel === reelId ? { ...item, [field]: value } : item
    //         )
    //     );
    // };






    const handleKeyPress = (event) => {
        if (event.altKey && event.key.toLowerCase() === 'p') {
            handleButtonClick('PrintQR')
        }
        if (event.altKey && event.key.toLowerCase() === 'a') {
            handleButtonClick('PrintQRAll')
        }
        if (event.altKey && event.key.toLowerCase() === 'b') {
            handleButtonClick('BookParentRoll')
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleButtonClick, handleButtonClick, handleButtonClick]);


    const toggleHotkeyInfo = () => {
        setShowHotkeyInfo(!showHotkeyInfo);
    };

    const callback = () => {
        setEditPartyID(0);
        setViewParentRollList(true);
        setAddParentRoll(false);
        setEditParentRollDetail([]);
        setSelectedMOs([]);
        setSelectedLotNumber([]);
        GetParentRollList();
        setViewProductionDetail(false);
    };


    const [filters, setFilters] = useState({
        date: '',
        pr_number: '',
        release_no: '',
        lot_no: '',
        reel_weight: '',
        status: '',
    });



    const handleOpen = () => {
        setOpenDateRangeDialog(true);
    };

    const handleClose = () => {
        setOpenDateRangeDialog(false);
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters({ ...filters, [name]: value });
    };


    const handleSelectAllClick = (event) => {

        if (event.target.checked) {
            const newSelectedRows = lotDetail.map((row) => row.id);
            setSelectedRows(newSelectedRows);

            let updatedRows;
            updatedRows = lotDetail.map(row => ({ ...row, selectitem: event.target.checked }));
            setLotDetail(updatedRows);

            const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
            setTotalMAQuantity(selected_ma_qty);
            setTotalReelLength(selected_reel_length);

            return;
        }

        setTotalMAQuantity(0);
        setTotalReelLength(0);
        setSelectedRows([]);


    };

    const handleRowCheckboxClick = (event, rowId) => {

        const selectedIndex = selectedRows.indexOf(rowId);
        let newSelectedRows = [];

        if (selectedIndex === -1) {
            newSelectedRows = [...selectedRows, rowId];
        } else if (selectedIndex === 0) {
            newSelectedRows = selectedRows.slice(1);
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelectedRows = selectedRows.slice(0, -1);
        } else if (selectedIndex > 0) {
            newSelectedRows = [
                ...selectedRows.slice(0, selectedIndex),
                ...selectedRows.slice(selectedIndex + 1),
            ];
        }

        setSelectedRows(newSelectedRows);

        const updatedRows = lotDetail.map(row =>
            row.id === rowId ? { ...row, selectitem: event.target.checked } : row
        );
        setLotDetail(updatedRows);

        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);

        if (newSelectedRows.length > 0) {
            setShowNewToolbar(true); // Show new toolbar when rows are selected
            setShowFirstToolbar(false);
        } else {
            setShowNewToolbar(false); // Hide new toolbar when no rows are selected
            setShowFirstToolbar(true);
        }
    };



    const filteredData = partyList.filter((row) => {
        // Check if row matches selected status
        if (selectedStatus === 'All' || row.status === selectedStatus) {
            // Check if all filter fields match
            return Object.keys(filters).every((key) => {
                if (filters[key] === '') return true; // No filter applied
                return String(row[key]).toLowerCase().includes(filters[key].toLowerCase());
            });
        }
        return false;
    });


    const handleShowNewVendor = () => {
        setEditPartyID(0);
        setViewParentRollList(false);
        setAddParentRoll(true);
        setSelectedMOs([]);
        setSelectedRows([]);
        setSelectedLotNumber([]);
        setViewQRCode(false);
        setMOHdrDetail([]);
        setLotNumbers([]);
        setIsEdit(false);
        setShiftDate(dayjs());
        setRemarks('');
        setStatus('');



        let newFromDate = dayjs().subtract(GlobalConfig.parentrollstartdate, 'day').format('DD/MMM/YYYY');
        let newToDate = dayjs().format('DD/MMM/YYYY')
        const p_companyid = localStorage.getItem('companyid');
        const p_frommodate = dayjs(newFromDate).format('YYYY-MM-DD hh:mm:ss');
        const p_tomodate = dayjs(newToDate).format('YYYY-MM-DD hh:mm:ss');
        const p_filtertype = "";
        const p_mono = 0;
        setParentRollMsgOpen(false);

        // Collect the values of the controls
        const orderData = {
            p_companyid,
            p_frommodate,
            p_tomodate,
            p_filtertype,
            p_mono,
            p_parent_roll_number: 'NA'
        };

        // Make the API call using the common function'http://localhost:3013/getMODetail'
        callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_MOLIST, 'POST', { 'Content-Type': 'application/json' }, orderData)
            .then(data => {
                if (data?.length > 0) {
                    // Update state with fetched data
                    setMOHdrDetail(data);
                    setViewMOList(true);
                    setViewProductionDetail(false);
                } else {
                    // No data found
                    setMOHdrDetail([]);
                    setViewMOList(false);
                    setViewMAList(false);
                    setViewQRCode(false);
                    setViewProductionDetail(false);
                }
            })
            .catch(error => {
                // Update state with error message      

                setViewMOList(false);
                setViewMAList(false);
                setViewQRCode(false);
                setViewProductionDetail(false);
            });
    }


    const handleShowQcPassModel = () => {
        setViewQCPassModel(true);
        setViewParentRollList(false);
    }

    const handleViewMAWiseBalanceQty = () => {
        setMAWiseBalanceList(true);
    }

    const handleQuantityChange = (index, quantity) => {
        const newRows = [...lotDetail];
        newRows[index].ma_qty = quantity;
        setLotDetail(newRows);

        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(newRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);
    };

    const handleLengthChange = (index, reel_length) => {
        const newRows = [...lotDetail];
        newRows[index].reel_length = reel_length;
        setLotDetail(newRows);

        const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(newRows);
        setTotalMAQuantity(selected_ma_qty);
        setTotalReelLength(selected_reel_length);
    };


    const handleCallEdit = async (value, parent_roll_number, status) => {
        if (!hasPermission(userPermissions, "Paper Machine Production", "Update")) {
            console.warn("User does not have permission to perform this action.");
            return; // Exit the function if permission is not granted
        }

        try {

            setEditPartyID(value);
            setViewParentRollList(false);
            setAddParentRoll(true);
            setLotnumberDisabled(true);
            await LoadParentRollDetails(value, parent_roll_number);
            setParentRollNumber(parent_roll_number);
            ParentRollProductDetail(parent_roll_number);
        }
        catch (error) {
            console.error("error in handlecallEdit:", error);
        }
    };

    // Load Parent Roll Details
    const LoadParentRollDetails = async (parent_roll_number, pr_number) => {
        const para = {
            p_companyid: localStorage.getItem('companyid'),
            id_parent_roll: parent_roll_number
        }

        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_PARENT_ROLL_DETAILS_USING_ID, 'POST', { 'Content-Type': 'application/json' }, para)
            if (data && data.length > 0) {
                setEditParentRollDetail(data);
                setShiftDate(data[0].shift_date);
                const remarksStr = data[0].remarks;

                if (remarks) {
                    // Convert string → JSON object
                    const remarks = typeof remarksStr === "string" ? JSON.parse(remarksStr) : remarksStr;

                    setRemarks(remarks);
                }

                setStatus(data[0].status);
                updateSelectedMONumber(data, parent_roll_number, pr_number);

                // check parent roll status lot wise
                let status = data.find((detail) => detail.status !== 'booked');
                setEnableRejectParentRoll(status ? true : false);
                LoadChildRollDetails(parent_roll_number);
            }
            else {
                setEditParentRollDetail([]);
                filterData('All');
            }

        }
        catch (error) {
            console.error("Error in LoadParentRollDetils:", error);
        }
    };

    // update selected MO Number value
    const updateSelectedMONumber = async (selectedData, parent_roll_number, pr_number) => {

        try {

            // group by data 
            const groupedSelectedData = selectedData.reduce((acc, current) => {
                const key = `${current.release_no}-${current.item_code}`;
                if (!acc[key]) {
                    acc[key] = current;
                }
                return acc;

            }, {})

            // converted the grouped data back to array
            const uniqueSelectedData = Object.values(groupedSelectedData);
            // Alert Message off
            setParentRollMsgOpen(false);

            // Collect the values of the controls
            const orderData = {
                p_companyid: localStorage.getItem('companyid'),
                p_frommodate: dayjs().subtract(4, 'year').startOf('year').format('YYYY-MM-DD HH:mm:ss'),
                p_tomodate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
                p_filtertype: "",
                p_mono: 3,
                p_parent_roll_number: pr_number
            };

            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_MOLIST, 'POST', { 'Content-Type': 'application/json' }, orderData);

            if (data && data.length > 0) {
                setMOHdrDetail(data);
                setViewMOList(true);
                setViewProductionDetail(false);

                let selectedMOvalues = [];
                uniqueSelectedData.forEach(selectedItem => {
                    // Find the matching MO based on the provided criteria
                    const selected = data.find(mo => mo.mono === selectedItem.release_no );
                    // && mo.itemcode === selectedItem.item_code
                    if (selected) {
                        // Check if the item is already selected
                        const isAlreadySelected = selectedMOs.some(mo => mo.mono === selected.mono );
                        // && mo.itemcode === selected.itemcode
                        if (!isAlreadySelected) {
                            // Add the new selection to the array
                            setSelectedMOs(prevSelected => [...prevSelected, selected]);
                        }
                        selectedMOvalues.push(selected);
                    }
                });

                if (selectedMOvalues.length > 0) {
                    const selectedMonos = selectedMOvalues.map(item => item.mono);
                    await callLoadLotNumbers(selectedMonos, "update", selectedData[0].shift_id, selectedData, selectedData[0].machine_id, parent_roll_number);
                }
            }
        } catch (error) {
            console.error("Error in updateSelectedMONumber:", error);
        }
    };


    const callLoadLotNumbers = async (selectedMonos, status, shiftID, selectedData, id_machine, parent_roll_number) => {

        if (selectedMonos.length > 0) {

            const orderData = {
                p_companyid: localStorage.getItem('companyid'),
                p_mono: selectedMonos,
            };

            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_LOTNUMBER_EDIT, 'POST', { 'Content-Type': 'application/json' }, orderData);

            if (data && data.length > 0) {

                // Update state with fetched data
                setLotNumbers(data);
                setViewMAList(true);
                setLotnumberDisabled(true);

                if (selectedData.length > 0 || status === "update") {
                    // grouped Lot numbers
                    const groupedMAList = selectedData.reduce((acc, current) => {
                        const key = `${current.release_no}-${current.lot_no}`;
                        if (!acc[key]) {
                            acc[key] = current;
                        }
                        return acc;
                    }, {})


                    // convert back to array list
                    const uniqueMOandLotNumber = Object.values(groupedMAList);
                    uniqueMOandLotNumber.forEach(selectedItem => {
                        // Find the matching MO based on the provided criteria
                        const selected = data.find(mo => mo.mono === selectedItem.release_no && mo.lotnumber === selectedItem.lot_no);
                        if (selected) {
                            // Check if the item is already selected
                            const isAlreadySelected = selectedLotNumber.some(mo => mo.mono === selected.mono && mo.itemcode === selected.lotnumber);
                            if (!isAlreadySelected) {
                                // Add the new selection to the array
                                setSelectedLotNumber(prevSelected => [...prevSelected, selected]);
                            }
                        }
                    });


                    const selectedMonos = uniqueMOandLotNumber.map((item) => item.release_no);
                    const selectedLotNumbers = uniqueMOandLotNumber.map((item) => item.lot_no);

                    await LoadLotWiseDetails(selectedMonos, selectedLotNumbers, parent_roll_number);
                    await loadShiftCode(shiftID);
                    await updateSelectedMachineIdValue(id_machine);
                    setLotDetailsUpdates(false);

                }



            } else {

                setLotNumbers([]);
                setViewMAList(false);
                setLotnumberDisabled(true);
                // alert('No MA Data are available in this MO.');
            }


        }
        else {
            setLotNumbers([]);
            setViewMAList(false);
            setLotnumberDisabled(true);
        }
    }


    useEffect(() => {
        if (editPartyID !== 0 && lotDetailsUpdates === false) {
            const newSelectedRows = lotDetail.map((row) => row.id);
            setSelectedRows(newSelectedRows);

            const updatedRows = lotDetail.map(row => ({ ...row, selectitem: true }));
            setLotDetail(updatedRows);

            const { selected_ma_qty, selected_reel_length } = calculateSelectedRowValue(updatedRows);
            setTotalMAQuantity(selected_ma_qty);
            setTotalReelLength(selected_reel_length);
            setLotDetailsUpdates(true);


            // details for qr code values
            const selectedMODetails = updatedRows.filter(row => row.selectitem);

            // Get unique selected Quality
            let selectedQuality = [...new Set(selectedMODetails.map(row => row.quality))];

            // Convert the array to a comma-separated string
            let selectedQualityString = selectedQuality.join(', ');

            setSelectedQuality(selectedQualityString);

            // Get unique selected GSM
            let selectedGSM = [...new Set(selectedMODetails.map(row => row.gsm))];

            // Convert the array to a comma-separated string
            let selectedGsmString = selectedGSM.join(', ');
            setSelectedGSM(selectedGsmString);
        }
    }, [lotDetail]); // Changed the dependency to lotdetail





    const handlecallProcess = (buttonName) => {
        if (buttonName === 'QRCode') {
            setViewWinderProduction(false);
            setViewReport(false);
            setViewQRCode(true);
        }
        else if (buttonName === 'WinderMachine') {
            setViewWinderProduction(true);
            setViewReport(false);
        }
        else if (buttonName === 'Reject') {
            setIsRejectionDialogOpen(true);
        }
        else if (buttonName == 'PDF') {
            setViewWinderProduction(false);
            setViewReport(true);


        }
        else if (buttonName === 'ViewParentRollDetail') {
            setViewWinderProduction(false);
            setViewQRCode(false);
            setViewReport(false);

        }
        else if (buttonName === 'ChildRoll') {
            setOpenChildRollList(true);
        }

        else if (buttonName === 'MAWiseBalanceQty') {
            setMAWiseBalanceList(true);
        }

    };


    // call back from winder machine
    const handleCallBackWinderMachine = () => {
        setViewWinderProduction(false);
    };


    const callRejectProcess = () => {
        alert('call reject rejection process started');
        setIsRejectionDialogOpen(false);
    };


    // get child roll status
    const LoadChildRollDetails = async (parent_roll_number) => {
        const para = { p_companyid: localStorage.getItem('companyid'), p_pr_number: parent_roll_number }
        try {
            const data = await callApi(API_ENDPOINTS.PROD_MANAGEMENT_GET_PARENT_ROLL_WISE_CHILD_LIST, 'POST', { 'Content-Type': 'application/json' }, para)
            setChildRollList(data?.length > 0 ? data : []);
        }
        catch (error) { console.error("Error in LoadParentRollDetils:", error); }
    };

    const closeChildRollList = () => {
        setOpenChildRollList(false);
        setMAWiseBalanceList(false);
    };


    // load Product Code details
    const ParentRollProductDetail = (ParentRollNumber) => {

        if (ParentRollNumber != '') {
            const orderData = {
                p_companyid: localStorage.getItem('companyid'),
                p_pr_number: ParentRollNumber,
            };

            callApi(API_ENDPOINTS.PROD_MANAGEMENT_SEARCH_GET_PARENT_ROLL_DETAIL, 'POST', { 'Content-Type': 'application/json' }, orderData)
                .then(data => { setSelectedProductDetails(data?.length > 0 ? data : []) })
                .catch(error => { });
        }

    };


    const PrintQRCode = (mo_number, lot_number) => {

    }

    // Calculate the number of pages
    const pageCount = Math.ceil(partyList.length / recordsPerPage);

    // Get the records for the current page
    const currentRecords = filteredData.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
    );

    const handleEdit = async (id_mo, id_ma, ma_qty, reel_length) => {
        const confirmDelete = window.confirm(`Are you sure you want to update the MA ${id_ma} details from the Parent Roll?`);
        if (confirmDelete) {
            // your update logic
            const payload = {
                p_companyid: localStorage.getItem('companyid'),
                p_plantid: 1,
                p_parent_roll_number: parentRollNumber,
                p_release_no: id_mo,
                p_lot_no: id_ma,
                p_user: localStorage.getItem('userID'),
                p_mode: lotDetail.length,
                p_reel_weight: ma_qty,
                p_reel_gross_weight: reel_length,
            }
            await updateMA(0, 0, payload);

            setSelectedLotNumber([]);
            handleCallEdit(editPartyID, parentRollNumber, '');
        }
    };

    const handleDelete = async (id_mo, id_ma) => {

        let msg = lotDetail.length > 1 ? `Are you sure you want to delete the MA ${id_ma} details from the Parent Roll?` : `Are you sure you want to delete the Parent Roll?`;
        const confirmDelete = window.confirm(msg);

        if (confirmDelete) {
            // your update logic
            const payload = {
                p_companyid: localStorage.getItem('companyid'),
                p_plantid: 1,
                p_parent_roll_number: parentRollNumber,
                p_release_no: id_mo,
                p_lot_no: id_ma,
                p_user: localStorage.getItem('userID'),
                p_mode: lotDetail.length,
            }
            await processIndent(0, 0, payload);

            if (lotDetail.length > 1) {

                setSelectedLotNumber([]);
                handleCallEdit(editPartyID, parentRollNumber, '');
            }
            else {
                handleShowNewVendor();
            }


        }
    };


    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    // filter oprations 
    const columns = ['date', 'pr_number', 'release_no', 'lot_no', 'reel_weight', 'status'];
    const autoCompleteColumns = ['date', 'pr_number', 'release_no', 'lot_no', 'status'];

    const handleFilterChangeTable = (e, column) => {
        setFilters({ ...filters, [column]: e.target.value });
    };

    const uniqueValues = {
        date: [...new Set(partyList.map(row => row.date))],
        pr_number: [...new Set(partyList.map(row => row.pr_number))],
        release_no: [...new Set(partyList.map(row => row.release_no))],
        lot_no: [...new Set(partyList.map(row => row.lot_no))],
        reel_weight: [...new Set(partyList.map(row => row.reel_weight))],
        status: [...new Set(partyList.map(row => row.status))],

    };

    const getStatusIcon = () => {
        switch (status?.toLowerCase()) {
            case 'hold':
                return (
                    <Tooltip title="Hold">
                        <PauseCircleOutlinedIcon sx={{ color: '#e57373' }} />
                    </Tooltip>
                );
            case 'booked':
                return (
                    <Tooltip title="Booked">
                        <DoneOutlinedIcon sx={{ color: '#fbc02d' }} />
                    </Tooltip>
                );
            case 'converted':
                return (
                    <Tooltip title="Converted">
                        <CheckCircleOutlineOutlinedIcon sx={{ color: '#66bb6a' }} />
                    </Tooltip>
                );
            default:
                return null;
        }
    };


    return (
        <Box>
            <CssBaseline />

            {viewWinderProduction && (<>
                <WinderMachineProduction editParentRollID={parentRollNumber} handleBackWinderMachineEntry={handleCallBackWinderMachine} />
            </>) || (
                    <>

                        {/* pop-up menus parent roll */}
                        <Dialog
                            open={openDateRangeDialog}
                            onClose={closepopup}
                            fullWidth
                            maxWidth="xs">
                            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
                                <QualityCheckRejectProcee rejectReelType={'PARENTROLL'} rejectReason={rejectReason} setRejectReason={setRejectReason} selectedReason={selectedReason} setSelectedReason={setSelectedReason} rejectProductType={rejectProductType} setRejectProductType={setRejectProductType} rejectStatus={rejectStatus} setRejectStatus={setRejectStatus} rejectReasonID={rejectReasonID} setRejectReasonID={setRejectReasonID} rejectRemarks={rejectRemarks} setRejectRemarks={setRejectRemarks} enableReject={enableReject} setEnableReject={setEnableReject} />
                            </DialogContent>
                            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
                                <Button onClick={closepopup} variant="contained">
                                    Update
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {/* pop-up menus break */}
                        <Dialog
                            open={openBreak}
                            onClose={closeBreakpopup}
                            fullWidth
                            maxWidth="xs"

                        >
                            <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
                                <ProductionBreakProcess rejectReelType={'PARENTROLL'} />
                            </DialogContent>
                            <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
                                <Button onClick={closeBreakpopup} variant="contained">
                                    close
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <ListOfChildRolls open={openChildRollList} onClose={closeChildRollList} childRolls={childRollList} />
                        <DialogBox open={openMAWiseBalanceList} onClose={closeChildRollList} modulename={'paper-roll-production-ma-wise-balance-qty'} />

                        {viewParentRollList && (
                            <>
                                <Divider />

                                <AppBar position="static" sx={{ backgroundColor: "inherit", borderRadius: "0px", height: "48px", justifyContent: "center" }}>
                                    <Toolbar>
                                        {/* First Menu */}
                                        <Button
                                            color="secondary"
                                            onClick={handleMenuClickFirst}
                                            endIcon={<ExpandMoreIcon sx={{ color: "teal", fontSize: "18px" }} />}
                                            sx={{ textTransform: 'none', fontSize: "16px", fontWeight: "500" }}
                                        >
                                            {selectedOptionFirst}
                                        </Button>
                                        <Menu
                                            anchorEl={anchorElFirst}
                                            open={openFirst}
                                            onClose={handleMenuCloseFirst}

                                        >
                                            <MenuItem onClick={() => handleMenuItemClickFirst('All Parent Roll List')}>All ParentRoll</MenuItem>
                                            <MenuItem onClick={() => handleMenuItemClickFirst('Inprogress Parent Roll')}>Inprogress Parent Roll</MenuItem>
                                            <MenuItem onClick={() => handleMenuItemClickFirst('Cancelled Parent Roll')}>Cancelled Parent Roll</MenuItem>
                                        </Menu>

                                        {/* Adding margin-left: auto to push the button to the right */}

                                        {hasPermission(userPermissions, "Paper Machine Production", "Create") && (
                                            <Button size="small" variant="contained" onClick={handleShowNewVendor} color="secondary" startIcon={<AddIcon sx={{ fontSize: "18px" }} />} sx={{ marginLeft: 'auto', textTransform: 'none' }}>New</Button>

                                        )}
                                        <Button size="small" variant="contained" onClick={handleShowQcPassModel} color="secondary" startIcon={<ApprovalIcon sx={{ fontSize: "18px" }} />} sx={{ ml: '5px', textTransform: 'none' }}>QC Approval</Button>
                                        <Button size="small" variant="contained" onClick={handleViewMAWiseBalanceQty} color="secondary" startIcon={<SummarizeIcon sx={{ fontSize: "18px" }} />} sx={{ ml: '5px', textTransform: 'none' }}>View MA Wise Balance Qty</Button>

                                        {/* Second Menu */}
                                        <IconButton
                                            size="small"
                                            aria-label="menu"
                                            sx={{ ml: 2 }}
                                            onClick={handleMenuClickSecond}
                                        >
                                            <MoreVertIcon sx={{ fontSize: "18px" }} />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorElSecond}
                                            open={openSecond}
                                            onClose={handleMenuCloseSecond}
                                        >
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Import Vendors')}>
                                                <ListItemIcon>
                                                    <ImportExportIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="Import Vendors" />
                                            </MenuItem>
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Export Vendors')}>
                                                <ListItemIcon>
                                                    <FileDownloadIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="Export Vendors" />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Export Current View')}>
                                                <ListItemIcon>
                                                    <ViewColumnIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="Export Current View" />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Preference List')}>
                                                <ListItemIcon>
                                                    <ListAltIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="Preference " />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Refresh List')}>
                                                <ListItemIcon>
                                                    <RefreshIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="Refresh List" />
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem onClick={() => handleMenuItemClickSecond('Reset Column Width')}>

                                                <Button variant="contained" color="secondary" size="small" startIcon={<FormatAlignJustifyIcon />}>
                                                    Reset Columns Width
                                                </Button>
                                            </MenuItem>
                                        </Menu>

                                        <IconButton
                                            size="small"
                                            aria-label="menu"
                                            sx={{ mr: 1, ml: 1 }}
                                            onClick={handleMenuClickSecond}
                                        >
                                            <HelpIcon sx={{ fontSize: "18px" }} />
                                        </IconButton>

                                        <div>
                                            <IconButton onClick={handleBackWinderMachineEntry} >
                                                <Close sx={{ fontSize: "18px", color: "#2196F3" }} />
                                            </IconButton>
                                        </div>
                                    </Toolbar>
                                </AppBar>

                                <Grid container padding="5px 0px 5px 5px" alignItems="center" sx={{ mt: 0, mb: 0, pt: 0, pb: 0, justifyContent: "flex-end" }} >
                                    {/* <Grid item xs={12} sm={12} md={1} lg={4} xl={2.8} mt="5px" display="flex-start" justifyContent="center">
                                       <Button>
                                        View MA Wise Balance Plan Qty
                                        </Button>                                         
                                    </Grid> */}


                                    <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1} mt="5px">
                                        <FormControl fullWidth size="small">
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    style={{ marginLeft: '10px' }}
                                                    id="FromDate"
                                                    value={dayjs(filterFromDate)}
                                                    onChange={(date) => handleFilterFromDate(date)}

                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            variant: 'standard', // Removes the filled/outlined border
                                                            InputProps: {
                                                                disableUnderline: true, // Remove underline from 'standard' variant
                                                            },
                                                            sx: {
                                                                backgroundColor: 'transparent',
                                                                padding: 0,
                                                                '& .MuiInputBase-root': {
                                                                    border: 'none',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '18px', // 👈 smaller icon size
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    paddingRight: '6px', // optional: reduce padding around icon
                                                                },
                                                            },
                                                        }
                                                    }}
                                                    renderInput={(props) => <TextField {...props} />}
                                                    autoFocus
                                                    format="DD-MMM-YYYY"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>
                                    <Grid item mt="5px" ml="10px" mr="10px">
                                        <Typography fontWeight="bold">-</Typography>
                                    </Grid>


                                    <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1} mt="5px" mr="10px">
                                        <FormControl fullWidth size="small" >
                                            <LocalizationProvider dateAdapter={AdapterDayjs} style={{ marginLeft: '10px' }}>
                                                <DatePicker
                                                    id="ToDate"
                                                    value={dayjs(filterToDate)}
                                                    format="DD-MMM-YYYY"
                                                    onChange={(date) => handleFilterToDate(date)}
                                                    // slotProps={{ textField: { size: 'small', } }}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            variant: 'standard', // Removes the filled/outlined border
                                                            InputProps: {
                                                                disableUnderline: true, // Remove underline from 'standard' variant
                                                            },
                                                            sx: {
                                                                backgroundColor: 'transparent',
                                                                padding: 0,
                                                                '& .MuiInputBase-root': {
                                                                    border: 'none',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '18px', // 👈 smaller icon size
                                                                },
                                                                '& .MuiInputBase-root': {
                                                                    paddingRight: '6px', // optional: reduce padding around icon
                                                                },
                                                            },
                                                        }
                                                    }}
                                                    renderInput={(props) => <TextField {...props} />}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>


                                </Grid>

                                <Divider sx={{ marginTop: "5px" }} />

                                <ResponsiveBox colors={colors} customMaxHeight={{ xs: '72vh', sm: '72vh', md: '74vh', lg: '85vh', xl: '100vh' }}>
                                    <CustomTableContainer colors={colors}>
                                        <TableContainer >
                                            {loading ? (
                                                <SkeletonTableLoader rows={10} columns={6} />
                                            ) : (
                                                <Table size="small">
                                                    <TableHead sx={{ backgroundColor: colors.primary[400] }}>
                                                        <TableRow>
                                                            <TableCell sx={{ textAlign: "left" }}>DATE</TableCell>
                                                            <TableCell sx={{ textAlign: "left" }}>PARENT ROLL NUMBER</TableCell>
                                                            <TableCell sx={{ textAlign: "left" }}>MO NUMBERS</TableCell>
                                                            <TableCell sx={{ textAlign: "left" }}>MA NUMBERS</TableCell>
                                                            <TableCell sx={{ textAlign: "left" }}>WEIGHT (TONS)</TableCell>
                                                            <TableCell sx={{ textAlign: "left" }}>STATUS</TableCell>

                                                            {/* <TableCell>
                                                                <IconButton onClick={handleOpen}>
                                                                    <Searchicon style={{ fontSize: "16px" }} />
                                                                </IconButton>
                                                                <Modal
                                                                    open={openDateRangeDialog}
                                                                    onClose={handleClose}
                                                                    aria-labelledby="modal-modal-title"
                                                                    aria-describedby="modal-modal-description"

                                                                >
                                                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: colors.primary[500], boxShadow: 24, p: 4, minWidth: 300 }}>
                                                                        <TextField
                                                                            label="Parent Roll Number"
                                                                            size="small"
                                                                            name="pr_number"
                                                                            value={filters.pr_number}
                                                                            onChange={handleFilterChange}
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            margin="normal"
                                                                        />
                                                                        <TextField
                                                                            label="MO Number"
                                                                            size="small"
                                                                            name="release_no"
                                                                            value={filters.release_no}
                                                                            onChange={handleFilterChange}
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            margin="normal"
                                                                        />

                                                                        <TextField
                                                                            label="Status"
                                                                            size="small"
                                                                            name="status"
                                                                            value={filters.status}
                                                                            onChange={handleFilterChange}
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            margin="normal"
                                                                        />
                                                                        <Button variant="contained" onClick={handleClose} style={{ marginTop: 10 }}>
                                                                            Apply Filter
                                                                        </Button>
                                                                    </Box>
                                                                </Modal>
                                                            </TableCell> */}
                                                        </TableRow>
                                                        <TableFilterRow
                                                            columns={columns}
                                                            filters={filters}
                                                            handleFilterChange={handleFilterChangeTable}
                                                            uniqueValues={uniqueValues}
                                                            autoCompleteColumns={autoCompleteColumns}
                                                        />
                                                    </TableHead>
                                                    <TableBody>
                                                        {currentRecords.map((row, index) => (
                                                            <TableRow key={row.id} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.background.default : alpha(theme.palette.action.hover, 0.04), }}>
                                                                <TableCell sx={{ textAlign: "center", width: '150px' }}>{row.date}</TableCell>
                                                                <TableCell sx={{ textAlign: "center", width: '150px' }}>
                                                                    <span
                                                                        style={{ color: 'teal', textDecoration: 'underline', cursor: 'pointer' }}
                                                                        onClick={() => handleCallEdit(row.id, row.pr_number, row.status)}
                                                                    >
                                                                        {row.pr_number}
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell sx={{ width: '250px' }} >{row.release_no}</TableCell>
                                                                <TableCell sx={{ width: '250px' }}>{row.lot_no}</TableCell>
                                                                <TableCell sx={{ textAlign: "right", width: '120px' }}>{row.reel_weight}</TableCell>
                                                                <TableCell sx={{ textAlign: "center", width: '120px' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: row.status === 'Booked' ? '#42a5f5'
                                                                                    : row.status === 'HOLD' ? '#f57c00'
                                                                                        : row.status === 'Converted' || row.status === 'completed' ? '#66bb6a'
                                                                                            : 'gray'
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ fontSize: '11px' }}>{row.status}</Typography>
                                                                    </Box>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            )}
                                            <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />
                                        </TableContainer>
                                    </CustomTableContainer>
                                </ResponsiveBox>
                            </>
                        )}

                        {viewQCPassModel && (<QCPassModel setViewParentRollList={setViewParentRollList} setViewQCPassModel={setViewQCPassModel} GetParentRollList={GetParentRollList} />)}

                        {addParentRoll && (
                            <>
                                <Header title={editPartyID === 0 ? 'Book Parent Roll' : 'Edit Parent Roll'} subtitle="" callback={callback} />
                                <Divider />
                                {editPartyID > 0 && (
                                    <>
                                        <ParentRollActionMenu selectedButtonName={selectedButtonName} setSelectedButtonName={setSelectedButtonName} handlecallProcess={handlecallProcess} enableRejectParentRoll={enableRejectParentRoll} />
                                        <Divider />
                                    </>
                                )}

                                {showHotkeyInfo && <HotkeyInfoBox />}

                                {/* Message box detail */}
                                <CallAlertBar parentRollMsgOpen={parentRollMsgOpen} setParentRollMsgOpen={setParentRollMsgOpen} parentRollSeverityvalue={parentRollSeverity}
                                    setParentRollSeverityvalue={setParentRollSeverity} parentRollAlertTitleValue={parentRollAlertTitle}
                                    setParentRollAlertTitleValue={setParentRollAlertTitle} parentRollMessageValue={parentRollMessage} setParentRollMessageValue={setParentRollMessage} />

                                {viewReport === true ? (
                                    <>
                                        <Box padding={5}>
                                            <ViewParentRollReport parentRollDetails={lotDetail} parentRollNumber={parentRollNumber} shiftDate={shiftDate} selectedShiftCodes={reportShiftCode} machineCode={reportMachineCode} totalMOQty={totalMAQuantity} totalLength={totalReelLength} />
                                        </Box>
                                    </>
                                ) : viewQRCode === true ? (<>
                                    <Grid container md={12} lg={7} xl={5}  >
                                        {viewQRCode && (
                                            <>
                                                <LabelParentRollDetails qrCodeValue={qrCodeValue} ProductDetail={selectedProductDetails} company_id={1} />

                                            </>
                                        )}
                                    </Grid>

                                </>) : viewQRCode === false ? (<>

                                    <ResponsiveBox customMaxHeight={{
                                        xs: '72vh', // Extra small devices (phones)
                                        sm: '72vh', // Small devices (tablets)
                                        md: '74vh', // Medium devices (small laptops)
                                        lg: '74vh', // Large devices (desktops)
                                        xl: '82vh', // Extra large devices (large desktops)
                                    }}>

                                        <Grid container  >
                                            {/* sx={{ backgroundColor: colors.primary[400] }} */}
                                            <Grid mt={1} item xs={12} sm={12} md={12} lg={12} xl={12}   >
                                                {editPartyID == 0 && (

                                                    <Grid mt={0} item xs={12} sm={12} md={12} lg={12} xl={12}   >
                                                        <Grid container padding="5px 0px 5px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center">
                                                            <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                                <Typography variant="h6" > {t('prod_winder_machine.filter_by')} </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={11} md={3.8} lg={1.5} xl={1.25}>
                                                                <FormControl fullWidth size="small">
                                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                        <DatePicker
                                                                            style={{ marginLeft: '10px' }}
                                                                            id="FromDate"
                                                                            value={dayjs(fromDate)}
                                                                            onChange={(date) => handleFromDateChange(date)}
                                                                            slotProps={{ textField: { size: 'small', } }}
                                                                            renderInput={(props) => <TextField {...props} />}
                                                                            autoFocus
                                                                            format="DD-MMM-YYYY"
                                                                        />
                                                                    </LocalizationProvider>
                                                                </FormControl>
                                                            </Grid>

                                                            <Grid item xs={12} sm={11} md={3.8} lg={1.5} xl={1.25}>
                                                                <FormControl fullWidth size="small" >
                                                                    <LocalizationProvider dateAdapter={AdapterDayjs} style={{ marginLeft: '10px' }}>
                                                                        <DatePicker
                                                                            id="ToDate"
                                                                            value={dayjs(toDate)}
                                                                            format="DD-MMM-YYYY"
                                                                            onChange={(date) => handleToDateChange(date)}
                                                                            slotProps={{ textField: { size: 'small', } }}
                                                                            renderInput={(props) => <TextField {...props} />}
                                                                        />
                                                                    </LocalizationProvider>
                                                                </FormControl>
                                                            </Grid>

                                                        </Grid>
                                                    </Grid>
                                                ) || (
                                                        <Grid container padding="0px 0px 5px 20px" sx={{ backgroundColor: colors.primary[400] }} spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" justifyItems="center">
                                                            <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                                <Typography variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                                    Parent Roll Number
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={11} md={3.8} lg={6} xl={6}>
                                                                <TextField
                                                                    size="small"
                                                                    value={parentRollNumber}
                                                                    // fullWidth
                                                                    InputProps={{
                                                                        endAdornment: (
                                                                            <InputAdornment position="end">
                                                                                {getStatusIcon()}
                                                                            </InputAdornment>
                                                                        ),
                                                                    }}
                                                                />
                                                            </Grid>

                                                        </Grid>

                                                    )}


                                                <Grid container padding="15px 0px 10px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center">
                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                        <Typography variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            MO Number
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={11} md={3.8} lg={7.6} xl={6}>
                                                        <Autocomplete
                                                            fullWidth
                                                            multiple
                                                            size="small"
                                                            id="mo-autocomplete"
                                                            disabled={isEdit}
                                                            options={moHdrDetail}
                                                            value={selectedMOs}
                                                            onChange={handleMOChange}
                                                            getOptionLabel={(option) => `${option.mono}-${option.itemcode}`} // Assuming lot numbers are strings
                                                            renderInput={(params) => (<TextField {...params} variant="outlined" label="MO Numbers" placeholder="Select MO Numbers" />)}
                                                        />
                                                    </Grid>

                                                </Grid>

                                                <Grid container padding="15px 0px 10px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center">
                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                        <Typography variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            MA Number
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={11} md={3.8} lg={7.6} xl={6}>
                                                        <Autocomplete
                                                            multiple
                                                            fullWidth
                                                            size="small"
                                                            options={lotNumbers} // Assuming lotNumbers is your options array                                                            
                                                            disabled={isEdit}
                                                            value={selectedLotNumber}
                                                            onChange={handleLotNumberChange}
                                                            getOptionLabel={(option) => `${option.mono}-${option.lotnumber}`} // Assuming lot numbers are strings
                                                            renderInput={(params) => (<TextField {...params} variant="outlined" label="Lot Numbers" placeholder="Select Lot Numbers" />)}
                                                        />
                                                    </Grid>

                                                </Grid>

                                            </Grid>

                                            {/* shift date */}

                                            <Grid item xs={12} xl={12} >
                                                <Grid container padding="10px 0px 0px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" >
                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                        <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            {t('prod_paper_machine.shift_date_time')}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={11} md={3.8} lg={2} xl={1.5}>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <Stack spacing={2}>
                                                                <DatePicker
                                                                    size="small"
                                                                    id="Orderdate"
                                                                    value={dayjs(shiftDate)}
                                                                    onChange={(date) => setShiftDate(date)}
                                                                    slotProps={{ textField: { size: 'small' } }}
                                                                    fullWidth
                                                                    disabled={isEdit}
                                                                    className="customDatePicker"
                                                                    format="DD-MMM-YYYY"
                                                                    renderInput={(props) => (
                                                                        <TextField
                                                                            helperText={shiftdate_errorMessage}
                                                                            error={shiftdate_errorMessage && shiftdate_errorMessage.length ? true : false}
                                                                            {...props}
                                                                        />
                                                                    )}
                                                                />
                                                            </Stack>
                                                        </LocalizationProvider>
                                                    </Grid>

                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} ml={2} display="flex-start" justifyContent="center">
                                                        <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            {t('prod_paper_machine.shift_code')}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={11} md={3.8} lg={1} xl={1}>

                                                        <FormControl fullWidth size="small">
                                                            <Autocomplete
                                                                fullWidth
                                                                value={selectedShiftCode}
                                                                onChange={handleShiftChange}
                                                                size="small"
                                                                style={{ fontSize: '12px' }}
                                                                options={shiftCodes}
                                                                disabled={isEdit}
                                                                getOptionLabel={(option) => `${option.shiftcode}`}
                                                                renderInput={(params) =>
                                                                    <TextField required

                                                                        helperText={errorMessage}
                                                                        error={errorMessage && errorMessage.length ? true : false}
                                                                        {...params} variant="outlined" />}
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                </Grid>
                                            </Grid>

                                            {/* paper machine */}
                                            <Grid item xs={12} xl={12} >
                                                <Grid container padding="20px 0px 0px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" >
                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                        <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            {t('prod_paper_machine.paper_machine')}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={11} md={3.8} lg={2} xl={1.5}>
                                                        <FormControl fullWidth size="small">
                                                            <Autocomplete
                                                                fullWidth
                                                                value={machineCode}
                                                                onChange={handleMachineChange}
                                                                size="small"
                                                                disabled={isEdit}
                                                                style={{ fontSize: '12px' }}
                                                                options={machines}
                                                                getOptionLabel={(option) => `${option.machine_code}`}
                                                                renderInput={(params) =>
                                                                    <TextField required
                                                                        helperText={errorMessage}
                                                                        error={errorMessage && errorMessage.length ? true : false}
                                                                        {...params} variant="outlined" />}
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} ml={2} display="flex-start" justifyContent="center">
                                                        <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            {t('prod_paper_machine.remarks')}
                                                        </Typography>
                                                    </Grid>

                                                    {/* <Grid item xs={12} sm={11} md={3.6} lg={3.2} xl={3.2}>
                                                        <Box>
                                                            <Autocomplete
                                                                size="small"
                                                                fullWidth
                                                                loading={loadingRemarks}
                                                                sx={{ flex: 1 }}
                                                                options={remarkOptions}
                                                                getOptionLabel={(option) => option?.description || ""}
                                                                value={remarks}
                                                                onChange={(event, newValue) => setRemarks(newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Remarks"
                                                                        variant="outlined"
                                                                    

                                                                    />
                                                                )}
                                                            />
                                                            <Tooltip title="Add New Remark">
                                                                <IconButton onClick={handleOpenAddRemark} size="small" sx={{ ml: 1 }}>
                                                                    <AddCircleOutlineIcon color="secondary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>

                                                    </Grid> */}

                                                    <Grid item xs={12} sm={11} md={3.8} lg={3.4} xl={3.4}>
                                                        <Box display="flex" alignItems="center">
                                                            <Autocomplete
                                                                size="small"
                                                                fullWidth
                                                                loading={loadingRemarks}
                                                                sx={{ flex: 1 }}
                                                                options={remarkOptions}
                                                                getOptionLabel={(option) => option?.description || ""}
                                                                value={remarks}
                                                                onChange={(event, newValue) => setRemarks(newValue)}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Remarks"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                            />

                                                            <Tooltip title="Add New Remark">
                                                                <IconButton
                                                                    onClick={handleOpenAddRemark}
                                                                    size="small"
                                                                    sx={{ ml: 1 }}
                                                                >
                                                                    <AddCircleOutlineIcon color="secondary" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Grid>


                                                </Grid>
                                            </Grid>

                                            {/* <Grid item xs={12} xl={12} >
                                                <Grid container padding="20px 0px 0px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" >
                                                    <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                        <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                            {t('prod_paper_machine.remarks')}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item xs={12} sm={11} md={8} lg={6} xl={5.5}>
                                                        <FormControl fullWidth size="small">
                                                            <TextField required
                                                                fullWidth
                                                                size="small"
                                                                value={remarks} onChange={(e) => (setRemarks(e.target.value))}
                                                                variant="outlined" />
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>
                                            </Grid> */}

                                            {/* {editPartyID > 0 && (
                                                <Grid item xs={12} xl={12} >
                                                    <Grid container padding="20px 0px 0px 20px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" >
                                                        <Grid item xs={12} sm={12} md={1.6} lg={1.5} xl={1} display="flex-start" justifyContent="center">
                                                            <Typography required variant="h6" fontSize={14} marginTop={0} textAlign="left">
                                                                {t('prod_paper_machine.pr_status')}
                                                            </Typography>
                                                        </Grid>

                                                        <Grid item xs={12} sm={11} md={8} lg={1} xl={1}>
                                                            <FormControl fullWidth size="small">
                                                                <TextField required
                                                                    fullWidth
                                                                    size="small"
                                                                    value={status} onChange={(e) => (setStatus(e.target.value))}
                                                                    variant="outlined" />
                                                            </FormControl>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            )} */}
                                            <Grid item xs={12} xl={12} mt={2} >
                                                {/* <Box position="sticky" top={0} zIndex={1100} bgcolor="background.paper" p={2}>
                                                    <Button variant="outlined">Reject Parent Roll</Button>
                                                    <Button variant="contained">Update Parent Roll</Button>
                                                </Box> */}
                                                <Divider />


                                            </Grid>


                                            <Grid item xs={12} lg={12} xl={12}  >

                                                <Grid container padding="0px 0px 0px 10px" spacing={{ xs: 2, sm: 2, md: 2, lg: 1, xl: 1 }} alignItems="center" >

                                                    <Grid item xs={12} sm={11} md={12} lg={11.8} xl={11.8} display="flex-start" justifyContent="center" >
                                                        {viewProductionDetail && (
                                                            <>
                                                                <CustomTableContainer colors={colors}>
                                                                    <TableContainer>
                                                                        <Table size="small" disabled={isEdit}>
                                                                            {/*  */}
                                                                            <TableHead sx={{ height: 10 }}>
                                                                                <TableRow>
                                                                                    <TableCell>
                                                                                        <Checkbox
                                                                                            size="small"
                                                                                            style={{ color: "secondary" }}
                                                                                            indeterminate={selectedRows.length > 0 && selectedRows.length < lotDetail.length}
                                                                                            checked={selectedRows.length === lotDetail.length}
                                                                                            onChange={handleSelectAllClick}
                                                                                            disabled={isEdit}
                                                                                        />
                                                                                    </TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>MO & MA Number</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>GSM</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Quality</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Jumbo Width</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Achieved Deckle</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Reel OD</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Core ID</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Total Sets</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>MA Qty (MT)</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Balance Qty (MT)</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Planned Qty (MT)</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Reel Length (meters)</TableCell>
                                                                                    <TableCell style={{ padding: '4px', textAlign: 'center' }}>Remarks</TableCell>

                                                                                    {editPartyID != 0 && (
                                                                                        <TableCell style={{ padding: '4px', textAlign: 'center' }}>Parent Roll #</TableCell>
                                                                                    )}

                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {lotDetail.map((row, index) => (
                                                                                    <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? theme.palette.background.default : alpha(theme.palette.action.hover, 0.04), }}>
                                                                                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'black', p: 0.5 }}>
                                                                                            <Checkbox
                                                                                                size="small"
                                                                                                checked={isSelected(row.id)}
                                                                                                onChange={(e) => handleRowCheckboxClick(e, row.id)}
                                                                                                disabled={isEdit}
                                                                                            />

                                                                                            {['HOLD', 'booked'].includes(row.status) && (
                                                                                                <>
                                                                                                    <Tooltip title="Edit">
                                                                                                        <IconButton size="small" onClick={() => handleEdit(row.mono, row.lotnumber, row.ma_qty, row.reel_length)}>
                                                                                                            <EditIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Tooltip>
                                                                                                    <Tooltip title="Delete">
                                                                                                        <IconButton size="small" onClick={() => handleDelete(row.mono, row.lotnumber)}>
                                                                                                            <DeleteIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Tooltip>
                                                                                                </>
                                                                                            )}
                                                                                        </TableCell>

                                                                                        {/* <TableCell sx={{ p: 0.5, border: 1, borderColor: 'black', display: 'flex', gap: 0.5 }}>
                                                                                            {(row.status === 'HOLD' || row.status === 'booked') && (
                                                                                                <>
                                                                                                    <Tooltip title="Edit">
                                                                                                        <IconButton size="small" onClick={() => handleEdit(row.mono, row.lotnumber)}>
                                                                                                            <EditIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Tooltip>
                                                                                                    <Tooltip title="Delete">
                                                                                                        <IconButton size="small" onClick={() => handleDelete(row.mono, row.lotnumber)}>
                                                                                                            <DeleteIcon fontSize="small" />
                                                                                                        </IconButton>
                                                                                                    </Tooltip>
                                                                                                </>
                                                                                            )}
                                                                                        </TableCell> */}

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="text"
                                                                                                value={`${row.mono} - ${row.lotnumber}`}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="text"
                                                                                                value={row.gsm}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="text"
                                                                                                value={row.quality}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.jumbowidth}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.achieved_deckle}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.reel_od}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.reel_id}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.noofsets}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                // value={row.qty}
                                                                                                value={parseFloat(row.qty).toFixed(3)}
                                                                                            />
                                                                                        </TableCell>
                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.balance_qty}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.ma_qty}
                                                                                                disabled={isEdit}
                                                                                                onChange={(e) => { handleQuantityChange(index, e.target.value); handleMaQtyChange(e, row.id) }}
                                                                                            />
                                                                                        </TableCell>

                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <TextField
                                                                                                fullWidth
                                                                                                disabled={isEdit}
                                                                                                size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="number"
                                                                                                value={row.reel_length}
                                                                                                onChange={(e) => { handleLengthChange(index, e.target.value); handleReelLengthChange(e, row.id) }}
                                                                                            />
                                                                                        </TableCell>
                                                                                        <TableCell style={{ padding: 2, border: '1px solid black', minWidth: "400px" }}>
                                                                                            {/* <TextField fullWidth disabled={isEdit} size="small"
                                                                                                sx={{
                                                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                                                        border: 'none',
                                                                                                    },
                                                                                                    '& .MuiInputBase-input': {
                                                                                                        padding: '4px',
                                                                                                        textAlign: 'center',
                                                                                                    },
                                                                                                }}
                                                                                                variant="outlined"
                                                                                                type="text"
                                                                                                value={row.remarks}
                                                                                                onChange={(e) => { handleChangeRemarksVal(e, row.id); }}
                                                                                            /> */}

                                                                                            <Box display="flex" alignItems="center">
                                                                                                <Autocomplete
                                                                                                    size="small"
                                                                                                    fullWidth
                                                                                                    loading={loadingRemarks}
                                                                                                    options={remarkOptions}   // full objects from API
                                                                                                    getOptionLabel={(option) => option?.description || ""}  // 👈 show only description
                                                                                                    // value={data.remarks || ""}  
                                                                                                    value={remarkOptions.find(opt => opt.description === row.remarks) || null}
                                                                                                    onChange={(_, newVal) => handleChangeRemarksValTable(row.id, "remarks", newVal.description)}
                                                                                                    renderInput={(params) => (
                                                                                                        <TextField {...params} variant="outlined" />
                                                                                                    )}
                                                                                                />

                                                                                                <Tooltip title="Add New Remark">
                                                                                                    <IconButton
                                                                                                        onClick={handleOpenAddRemark}
                                                                                                        size="small"
                                                                                                        sx={{ ml: 1 }}
                                                                                                    >
                                                                                                        <AddCircleOutlineIcon color="secondary" />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            </Box>
                                                                                        </TableCell>




                                                                                        <TableCell style={{ padding: 2, border: '1px solid black' }}>
                                                                                            <span
                                                                                                style={{ color: 'teal', textDecoration: 'underline', cursor: 'pointer' }}
                                                                                                onClick={() => PrintQRCode(row.mono, row.lotnumber)}
                                                                                            >
                                                                                                {row.parent_roll_seq}
                                                                                            </span>
                                                                                        </TableCell>
                                                                                    </TableRow>

                                                                                ))}

                                                                            </TableBody>



                                                                        </Table>
                                                                        {/* table footer value */}
                                                                        <Box padding={1} display="flex" justifyContent="space-between" backgroundColor={colors.primary[400]} mt={2}>
                                                                            <Typography variant="h6">
                                                                                Parent Roll Weight: <strong>{totalMAQuantity}</strong>
                                                                            </Typography>
                                                                            <Typography variant="h6">
                                                                                Parent Roll Length: <strong>{totalReelLength}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableContainer>
                                                                </CustomTableContainer>
                                                            </>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </ResponsiveBox>
                                </>) : null}
                            </>
                        )}

                        {openAddRemark && (
                            <AddRemarks open={openAddRemark} handleClose={handleCloseAddRemark} moduleName={"Paper Machine Production"} parentModuleName={"Production"} userPermissions={userPermissions} />
                        )}


                        <Grid container>
                            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} >
                                <Box position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: "transparent" }}>
                                    <Toolbar>
                                        {viewProductionDetail && (
                                            <>
                                                {editPartyID === 0 && (
                                                    <div>

                                                        {isSaveButtonDisabled && (<> <Button size="small" onClick={() => handleShowNewVendor()} variant="contained" color="secondary" startIcon={<AddIcon />} sx={{ marginLeft: 'auto', textTransform: 'none' }}>Book New Parent Roll</Button> </>)}
                                                        {isSaveButtonDisabled === false && (<><Button type="submit" disabled={isSaveButtonDisabled} size="small" onClick={() => handleButtonClick('BookParentRoll')} variant="contained" color="secondary" startIcon={<DoneIcon />} sx={{ marginLeft: 'auto', textTransform: 'none' }}>Book Parent Roll</Button> </>)}
                                                        <Button size="small" variant="standard" startIcon={<Cancel />} onClick={() => callback()} sx={{ marginLeft: '5px', textTransform: 'none' }}>Cancel</Button>
                                                    </div>
                                                ) || (
                                                        <> <Button size="small" onClick={() => handleShowNewVendor()} variant="contained" color="secondary" startIcon={<AddIcon />} sx={{ marginLeft: 'auto', textTransform: 'none' }}>Book New Parent Roll</Button>
                                                            <Button size="small" disabled={isEdit} onClick={() => handleButtonClick('UpdateParentRoll')} variant="contained" color="secondary" startIcon={<UpdateIcon />} sx={{ marginLeft: '5px', textTransform: 'none' }}>Update Parent Roll</Button> </>
                                                    )
                                                }
                                            </>
                                        )}
                                    </Toolbar>
                                </Box>
                            </Grid>
                        </Grid>

                    </>
                )
            }

            <ConfirmationMessage open={isRejectionDialogOpen} onClose={() => { setIsRejectionDialogOpen(false); }} message={'Do you really want reject to reject this Parent roll ?'} buttonname={'Reject'} action={callRejectProcess} />

        </Box >
    )
}

export default PaperRollProduction;